import { faFilter } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, ButtonGroup, FormHelperText, TextField, Typography } from '@material-ui/core'
import { Pill } from '@scoir/pills'

import { useCurrentSeniorYear } from 'store/current-senior-year/hooks'

import {
  CLASSES,
  Class,
  HISTORIC_FOLLOWER_YEARS,
  HistoricFollowerYear,
  formatSelectedClassesLabel,
  formatSelectedHistoricFollowerYearsLabel,
} from 'routes/highschools/ExploreTable/gradYears'
import { UIFilters } from 'routes/highschools/types'

import { numericValueOrDefault, parseNumeric } from '.'
import { PremiumWrapper } from '../PremiumPreview/PremiumWrapper'

type FilterStudentEngagementProps = {
  interestedHandraisers?: {
    min?: number
    max?: number
  }
  interestedFollowers?: {
    min?: number
    max?: number
  }
  historicFollowers?: {
    min?: number
    max?: number
  }
  hasCounselorSuggestions?: boolean
  selectedHandraiserClasses?: Class[]
  selectedFollowerClasses?: Class[]
  selectedHistoricFollowerYears?: HistoricFollowerYear[]
  onChange: (formState: UIFilters) => void
  errors: {
    interestedHandraisers?: {
      helperText: string
    }
    interestedFollowers?: {
      helperText: string
    }
    historicFollowers?: {
      helperText: string
    }
  }
  enabled: boolean
}

const FilterStudentEngagement = ({
  interestedHandraisers,
  interestedFollowers,
  historicFollowers,
  hasCounselorSuggestions,
  selectedHandraiserClasses = [...CLASSES],
  selectedFollowerClasses = [...CLASSES],
  selectedHistoricFollowerYears = [...HISTORIC_FOLLOWER_YEARS],
  onChange,
  errors,
  enabled,
}: FilterStudentEngagementProps) => {
  const currentSeniorYear = useCurrentSeniorYear()
  const handraiserClassesSummary = formatSelectedClassesLabel(selectedHandraiserClasses)
  const followerClassesSummary = formatSelectedClassesLabel(selectedFollowerClasses)
  const historicFollowerYearsSummary = formatSelectedHistoricFollowerYearsLabel(
    selectedHistoricFollowerYears,
    currentSeniorYear,
  )
  return (
    <Box
      display="flex"
      flexDirection="column"
      gridGap={24}
    >
      <Typography
        component="div"
        variant="h6"
      >
        Scoir Engagement
      </Typography>
      <PremiumWrapper
        enabled={enabled}
        trackingIdPlacement="scoir-engagement-filter"
      >
          <>
            <Box>
              <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                gridGap={8}
              >
                <Box flex={1}>
                  <Typography variant="subtitle2">Handraisers</Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    Students who opted in to share their contact information with you.
                  </Typography>
                </Box>
                <Box
                  flexShrink={0}
                  style={selectedHandraiserClasses.length === 0 ? { opacity: 0.6 } : undefined}
                >
                  <Pill color={selectedHandraiserClasses.length === 0 ? 'text' : 'info'}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gridGap={8}
                    >
                      <FontAwesomeIcon icon={faFilter} />
                      <Typography variant="caption">{handraiserClassesSummary}</Typography>
                    </Box>
                  </Pill>
                </Box>
              </Box>
              <Box
                display="flex"
                gridGap={16}
                pt={1}
              >
                <Box flexBasis="50%">
                  <TextField
                    disabled={!enabled}
                    fullWidth
                    id="min-interested-handraisers"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="Min students"
                    value={numericValueOrDefault(interestedHandraisers?.min)}
                    inputProps={{ min: 0, max: interestedHandraisers?.max }}
                    onChange={(e) => {
                      const value = parseNumeric(e.target.value)
                      onChange({
                        interestedHandraisers: {
                          ...interestedHandraisers,
                          min: value,
                        },
                      })
                    }}
                    error={!!errors.interestedHandraisers}
                  />
                </Box>
                <Box flexBasis="50%">
                  <TextField
                    disabled={!enabled}
                    fullWidth
                    id="max-interested-handraisers"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="Max students"
                    value={numericValueOrDefault(interestedHandraisers?.max)}
                    inputProps={{ min: interestedHandraisers?.min || 0 }}
                    onChange={(e) => {
                      const value = parseNumeric(e.target.value)
                      onChange({
                        interestedHandraisers: {
                          ...interestedHandraisers,
                          max: value,
                        },
                      })
                    }}
                    error={!!errors.interestedHandraisers}
                  />
                </Box>
              </Box>
              <FormHelperText error={!!errors.interestedHandraisers}>
                {errors.interestedHandraisers?.helperText}
              </FormHelperText>
            </Box>
            <Box>
              <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                gridGap={8}
              >
                <Box flex={1}>
                  <Typography variant="subtitle2">Followers</Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    Students following your college on Scoir.
                  </Typography>
                </Box>
                <Box
                  flexShrink={0}
                  style={selectedFollowerClasses.length === 0 ? { opacity: 0.6 } : undefined}
                >
                  <Pill color={selectedFollowerClasses.length === 0 ? 'text' : 'info'}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gridGap={8}
                    >
                      <FontAwesomeIcon icon={faFilter} />
                      <Typography variant="caption">{followerClassesSummary}</Typography>
                    </Box>
                  </Pill>
                </Box>
              </Box>
              <Box
                display="flex"
                gridGap={16}
                pt={1}
              >
                <Box flexBasis="50%">
                  <TextField
                    disabled={!enabled}
                    fullWidth
                    id="min-interested-followers"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="Min students"
                    value={numericValueOrDefault(interestedFollowers?.min)}
                    inputProps={{ min: 0, max: interestedFollowers?.max }}
                    onChange={(e) => {
                      const value = parseNumeric(e.target.value)
                      onChange({
                        interestedFollowers: {
                          ...interestedFollowers,
                          min: value,
                        },
                      })
                    }}
                    error={!!errors.interestedFollowers}
                  />
                </Box>
                <Box flexBasis="50%">
                  <TextField
                    disabled={!enabled}
                    fullWidth
                    id="max-interested-followers"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="Max students"
                    value={numericValueOrDefault(interestedFollowers?.max)}
                    inputProps={{ min: interestedFollowers?.min || 0 }}
                    onChange={(e) => {
                      const value = parseNumeric(e.target.value)
                      onChange({
                        interestedFollowers: {
                          ...interestedFollowers,
                          max: value,
                        },
                      })
                    }}
                    error={!!errors.interestedFollowers}
                  />
                </Box>
              </Box>
              <FormHelperText error={!!errors.interestedFollowers}>
                {errors.interestedFollowers?.helperText}
              </FormHelperText>
            </Box>
            <Box>
              <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                gridGap={8}
              >
                <Box flex={1}>
                  <Typography variant="subtitle2">Historic Followers</Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    Alumni who followed your college on Scoir.
                  </Typography>
                </Box>
                <Box
                  flexShrink={0}
                  style={selectedHistoricFollowerYears.length === 0 ? { opacity: 0.6 } : undefined}
                >
                  <Pill color={selectedHistoricFollowerYears.length === 0 ? 'text' : 'info'}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gridGap={8}
                    >
                      <FontAwesomeIcon icon={faFilter} />
                      <Typography variant="caption">{historicFollowerYearsSummary}</Typography>
                    </Box>
                  </Pill>
                </Box>
              </Box>
              <Box
                display="flex"
                gridGap={16}
                pt={1}
              >
                <Box flexBasis="50%">
                  <TextField
                    disabled={!enabled}
                    fullWidth
                    id="min-historic-followers"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="Min students"
                    value={numericValueOrDefault(historicFollowers?.min)}
                    inputProps={{ min: 0, max: historicFollowers?.max }}
                    onChange={(e) => {
                      const value = parseNumeric(e.target.value)
                      onChange({
                        historicFollowers: {
                          ...historicFollowers,
                          min: value,
                        },
                      })
                    }}
                    error={!!errors.historicFollowers}
                  />
                </Box>
                <Box flexBasis="50%">
                  <TextField
                    disabled={!enabled}
                    fullWidth
                    id="max-historic-followers"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="Max students"
                    value={numericValueOrDefault(historicFollowers?.max)}
                    inputProps={{ min: historicFollowers?.min || 0 }}
                    onChange={(e) => {
                      const value = parseNumeric(e.target.value)
                      onChange({
                        historicFollowers: {
                          ...historicFollowers,
                          max: value,
                        },
                      })
                    }}
                    error={!!errors.historicFollowers}
                  />
                </Box>
              </Box>
              <FormHelperText error={!!errors.historicFollowers}>{errors.historicFollowers?.helperText}</FormHelperText>
            </Box>
          </>
        <Box>
          <Typography variant="subtitle2">Promoter School</Typography>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            High schools where Scoir has identified counselors suggesting your college to students.
          </Typography>
          <Box pt={1}>
            <ButtonGroup
              disableFocusRipple
              color="primary"
            >
              <Button
                variant={hasCounselorSuggestions === true ? 'contained' : 'outlined'}
                onClick={() =>
                  hasCounselorSuggestions === true
                    ? onChange({ hasCounselorSuggestions: undefined })
                    : onChange({ hasCounselorSuggestions: true })
                }
              >
                Yes
              </Button>
              <Button
                variant={hasCounselorSuggestions === false ? 'contained' : 'outlined'}
                onClick={() =>
                  hasCounselorSuggestions === false
                    ? onChange({ hasCounselorSuggestions: undefined })
                    : onChange({ hasCounselorSuggestions: false })
                }
              >
                No
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </PremiumWrapper>
    </Box>
  )
}

export default FilterStudentEngagement
